import { Route } from 'react-router-dom';
import '@screentone/core/css/index.css';
import '@screentone/addon-auth-wrapper/css/index.css';
import 'theme/global.scss';
import { ProtectedRoute } from '@screentone/addon-auth-wrapper';

import { PropertyRedirect } from 'components/PropertyRedirect';
import { AlertProvider } from 'contexts/alert/AlertContext';
import { AppQueryProvider } from 'data/query-client';
import { OktaRouter } from 'features/auth';
import ContentParameterBulkEdit from 'features/content-parameter-bulk-edit/ContentParameterBulkEdit';
import { Layouts } from 'features/layouts/Layouts';
import MobileIssueManagement from 'features/mobile-issue-management/MobileIssueManagement';
import PublicationSettingsContentSources from 'features/publication-settings-content-sources/PublicationSettingsContentSources';
import PublicationSettingsDisableContentPresentationParameters from 'features/publication-settings-disable-content-presentation-parameters/PublicationSettingsDisableContentPresentationParameters';
import PublicationSettingsDisableWSJTranslation from 'features/publication-settings-disable-wsj-translation/PublicationSettingsDisableWSJTranslation';
import PublicationSettingsItpRequiredPageTypes from 'features/publication-settings-itp-required-page-types/PublicationSettingsItpRequiredPageTypes';
import PublicationSettingsPubcrawl from 'features/publication-settings-pubcrawl/PublicationSettingsPubcrawl';
import PublicationSettingsPublishingRules from 'features/publication-settings-publishing-rules/PublicationSettingsPublishingRules';
import PublicationSettingsRulesConfig from 'features/publication-settings-rules-config/PublicationSettingsRulesConfig';
import PublicationSettingsSetup from 'features/publication-settings-setup/PublicationSettingsSetup';
import BannerEditPage from 'pages/BannerEditPage';
import BannersPage from 'pages/BannersPage';
import CollectionEditPage from 'pages/CollectionEditPage';
import CollectionsPage from 'pages/CollectionsPage';
import DebugPage from 'pages/DebugPage';
import HealthPage from 'pages/HealthPage';
import ItpEditPage from 'pages/ItpEditPage';
import ItpFeedPage from 'pages/ItpFeedPage';
import MultiCollectionEditPage from 'pages/MultiCollectionEditPage';
import NewSectionPage from 'pages/new-section-page/NewSectionPage';
import NewsletterEditPage from 'pages/NewsletterEditPage';
import NewsletterPage from 'pages/NewsletterPage';
import OffPlatformEditPage from 'pages/OffPlatformEditPage';
import OffPlatformPage from 'pages/OffPlatformPage';
import SavedNewsletterPage from 'pages/SavedNewsletterPage';
import ScheduledNewsletterPage from 'pages/ScheduledNewsletterPage';
import SectionPageEditPage from 'pages/SectionPageEditPage';
import SiteConfig from 'pages/site-config';
import CollectionSettings from 'pages/site-config/collections-settings';
import SiteConfigContentPresentationConfigDefaults from 'pages/site-config/content-presentation/config-defaults';
import SiteConfigContentPresentationList from 'pages/site-config/content-presentation/list';
import SiteConfigContentPresentationListDetail from 'pages/site-config/content-presentation/list-detail';
import SiteConfigContentPresentationNew from 'pages/site-config/content-presentation/new';
import SiteConfigFeatureFlags from 'pages/site-config/feature-flags';
import SiteConfigGeneral from 'pages/site-config/general';
import SiteConfigGlobalSettings from 'pages/site-config/global-settings';
import SiteConfigLayouts from 'pages/site-config/layouts';
import SiteConfigLayoutEdit from 'pages/site-config/layouts/edit';
import SiteConfigLayoutsGeneral from 'pages/site-config/layouts/general';
import SiteConfigLayoutsSectionParameters from 'pages/site-config/layouts/section-parameters';
import SiteConfigMobileAndApp from 'pages/site-config/mobile-and-app';
import SiteConfigMobileIssueManagementEdit from 'pages/site-config/mobile-issue-management-edit';
import SiteConfigModules from 'pages/site-config/modules';
import SiteConfigPageTypes from 'pages/site-config/page-types';
import SiteConfigQueryRules from 'pages/site-config/query-rules';
import AppErrorBoundary from './AppErrorBoundary';

const Routing = () => (
  <OktaRouter>
    <Route path="/_health" element={<HealthPage />} />
    <Route
      path="/:property/debug"
      element={
        <ProtectedRoute path="/:property/debug">
          <DebugPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/debug/:allessehId"
      element={
        <ProtectedRoute path="/:property/debug/:allessehId">
          <DebugPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/multicollection/:allessehId"
      element={
        <ProtectedRoute path="/:property/multicollection/:allessehId">
          <MultiCollectionEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/collections"
      element={
        <ProtectedRoute path="/:property/collections">
          <CollectionsPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/collections/:idCreatedUtc"
      element={
        <ProtectedRoute path="/:property/collections/:idCreatedUtc">
          <CollectionEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/issues"
      element={
        <ProtectedRoute path="/:property/issues">
          <ItpFeedPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/issues/:issueDate"
      element={
        <ProtectedRoute path="/:property/issues/:issueDate">
          <ItpEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/off-platform"
      element={
        <ProtectedRoute path="/:property/off-platform">
          <OffPlatformPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/off-platform/:idCreatedUtc"
      element={
        <ProtectedRoute path="/:property/off-platform/:idCreatedUtc">
          <OffPlatformEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/pages/:allessehIdOrSlug"
      element={
        <ProtectedRoute path="/:property/pages/:allessehIdOrSlug">
          <SectionPageEditPage />
        </ProtectedRoute>
      }
    >
      <Route path="version/:publishUtc" element={<></>} />
    </Route>
    <Route
      path="/:property/pages/new"
      element={
        <ProtectedRoute path="/:property/pages/new">
          <NewSectionPage />
        </ProtectedRoute>
      }
    >
      <Route path="version/:publishUtc" element={<></>} />
    </Route>
    <Route
      path="/:property"
      element={
        <ProtectedRoute path="/:property">
          <PropertyRedirect />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/newsletters"
      element={
        <ProtectedRoute path="/:property">
          <NewsletterPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/newsletters/:campaignId"
      element={
        <ProtectedRoute path="/:property">
          <SavedNewsletterPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/newsletters/scheduled/:campaignId/:publishUtc"
      element={
        <ProtectedRoute path="/:property">
          <ScheduledNewsletterPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/newsletters/new"
      element={
        <ProtectedRoute path="/:property">
          <NewsletterEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/banners"
      element={
        <ProtectedRoute path="/:property/banners">
          <BannersPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/banners/:allessehId"
      element={
        <ProtectedRoute path="/:property/banners/:allessehId">
          <BannerEditPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/:property/site-config"
      element={
        <ProtectedRoute path="/:property/site-config">
          <SiteConfig />
        </ProtectedRoute>
      }
    >
      <Route element={<SiteConfigGeneral />}>
        <Route index element={<PublicationSettingsSetup />} />
        <Route path="publishing-rules" element={<PublicationSettingsPublishingRules />} />
        <Route path="content-sources" element={<PublicationSettingsContentSources />} />
      </Route>
      <Route path="modules" element={<SiteConfigModules />} />
      <Route path="layouts" element={<SiteConfigLayouts />}>
        <Route index element={<Layouts />} />
        <Route path="section-parameters" element={<SiteConfigLayoutsSectionParameters />} />
        <Route path="general" element={<SiteConfigLayoutsGeneral />} />
      </Route>
      <Route path="page-types" element={<SiteConfigPageTypes />} />
      <Route path="query-rules" element={<SiteConfigQueryRules />} />
      <Route path="mobile-and-app" element={<SiteConfigMobileAndApp />}>
        <Route index element={<ContentParameterBulkEdit />} />
        <Route path="rules-config" element={<PublicationSettingsRulesConfig />} />
        <Route path="mobile-issues" element={<MobileIssueManagement />} />
        <Route path="pubcrawl" element={<PublicationSettingsPubcrawl />} />
        <Route path="disable-wsj-translation" element={<PublicationSettingsDisableWSJTranslation />} />
        <Route path="itp-required-page-types" element={<PublicationSettingsItpRequiredPageTypes />} />
        <Route
          path="disable-content-presentation-parameters"
          element={<PublicationSettingsDisableContentPresentationParameters />}
        />
      </Route>
      <Route path="mobile-and-app/content-presentation/new" element={<SiteConfigContentPresentationNew />} />
      <Route path="mobile-and-app/content-presentation/list" element={<SiteConfigContentPresentationList />} />
      <Route
        path="mobile-and-app/content-presentation/list/:allessehId"
        element={<SiteConfigContentPresentationListDetail />}
      />
      <Route
        path="mobile-and-app/content-presentation/config-defaults"
        element={<SiteConfigContentPresentationConfigDefaults />}
      />
      <Route path="mobile-and-app/mobile-issues/:mobileIssueId" element={<SiteConfigMobileIssueManagementEdit />} />
      <Route path="global-settings" element={<SiteConfigGlobalSettings />} />
      <Route path='feature-flags' element={<SiteConfigFeatureFlags />} />
      <Route
        path="/:property/site-config/collections"
        element={
          <ProtectedRoute path="/:property/site-config/collections">
            <CollectionSettings />
          </ProtectedRoute>
        }
      />
    </Route>
    <Route
      path="/:property/site-config/layouts/:artifactId"
      element={
        <ProtectedRoute path="/:property/site-config/layouts/:artifactId">
          <SiteConfigLayoutEdit />
        </ProtectedRoute>
      }
    />
  </OktaRouter>
);

const App = () => (
  <AppQueryProvider>
    <AlertProvider>
      <AppErrorBoundary>
        <Routing />
      </AppErrorBoundary>
    </AlertProvider>
  </AppQueryProvider>
);

export default App;
